import omit from 'lodash/omit'
import React from 'react'
import Tooltip from 'react-bootstrap/Tooltip'
import { useDispatch, useSelector } from 'react-redux'
import * as yup from 'yup'
import {
  Accordion,
  Dropdown,
  FormControl,
  FormGroup,
  FormLabel,
  Popper,
} from '../../../../components'
import Button from '../../../../components/Button'
import { COUNTRY } from '../../../../utils/constants'
import { concatClasses, isOneDropdownOption } from '../../../../utils/fns'
import { useLogicButton, useTextButton } from '../../../../utils/useForm'
import { formatNumber } from '../../../../utils/utils'
import {
  ANCHORAGE_OPTION,
  COMPRESSIVE_STRENGTH,
  CORROSION_RESISTANCE_VALUE,
  GEOMETRY_TYPE,
  HELP_IMG,
  HOLDOWN_SUBTYPE,
  HOLDOWN_LABELS,
  LOAD_TYPE_CANADA,
  MATERIAL_TYPE,
  POST_BASE_DEFAULT_STATE,
  POST_BASE_FASTENERS_TYPE,
  POST_BASE_LABELS,
  POST_BASE_LUMBER_SPECIES,
  POST_BASE_MATERIAL_TYPE,
  POST_BASE_SUBTYPE,
  POST_BASE_SUBTYPE_LABEL,
  anchorageOptions,
  holdownLumberSpeciesOptions,
  materialTypeOptions,
  postBaseFastenersTypeOptions,
  postBaseLumberSpeciesOptions,
  postBaseMaterialTypeOptions,
  FOUNDATION_TYPE,
  HOLDOWN_FASTENERS,
  DOWNLOAD_DURATION,
  POST_WIDTH_POST_BASE_DEFAULT,
  POST_THICKNESS_HOLDOWN_DEFAULT,
  UNITS,
  POST_BASE_TYPE_IMG_SRC,
  HOLD_DOWN_TYPE_IMG_SRC,
  HOLDOWN_TYPE,
  INPUT_NAME,
  MEASUREMENT_UNITS,
} from '../../common/constants'
import {
  geometryTypeImages,
  getGeometrySubTypeOptions,
  getGeometryTypeOptions,
  getLumberSpeciesDefaultValue,
  getMinStemwallThicknessOptions,
  getDefaultOptValue,
  getPostWidthOptions,
  getPostThicknessOptions,
} from '../../common/fns'
import {
  compressiveStrengthData,
  corrosionResistanceOptions,
  foundationTypeData,
  helpText,
  holdownFastenersTypeData,
  installOverSheathingData,
  loadTypeData,
  postBaseGeometrySubTypeData,
  postBaseLoadTypeData,
  postTypeData,
  raisedFloorData,
  upliftDurationData,
  wallThicknessData,
  downloadDurationData,
  installationTypeData,
  postWidthData,
  postThicknessData,
} from '../../common/json/staticData.json'
import { InputSchema } from '../../common/validation'
import { inputActions, inputSlice, selectInputState } from '../../store/slices/input.slice'
import { selectOutputStatus } from '../../store/slices/output.slice'
import { CountrySelection } from './components'

const Input = (props) => {
  const dispatch = useDispatch()
  const inputState = useSelector(selectInputState)
  const isLoading = useSelector((state) => selectOutputStatus(state).isLoading)

  const {
    holdownDesign,
    foundationDesign,
    postBaseDesign,
    upliftLoadASD,
    downloadASD,
    loadType,
    holdownSubType,
    postBaseSubType,
    lumberSpecies,
    postBaseLumberSpecies,
    wallThickness,
    installOverSheathing,
    holdownFastenersType,
    postBaseFastenersType,
    compressiveStrength,
    anchorageOption,
    raisedFloor,
    materialType,
    postBaseMaterialType,
    geometryType,
    geometrySubType,
    postBaseGeometrySubType,
    validation,
    corrosionResistance,
    lateralF1,
    lateralF2,
    postBaseLoadType,
    settings: { country },
    holdownType,
    postBaseType,
    foundationType,
    upliftDuration,
    factoredUpliftLRFD,
    factoredDownloadLRFD,
    minStemwallThickness,
    downloadDuration,
    postWidth,
    postThickness,
    installationType,
  } = inputState

  const handleInputChange = (e) => {
    const { name, value } = e.target
    const convertValue = value.replace(/,/g, '')
    try {
      yup.reach(InputSchema, name).validateSync(convertValue, {
        context: {
          country: country,
        },
      })
      dispatch(inputActions.removeFieldError(name))
    } catch (error) {
      dispatch(inputActions.setFieldError({ name, message: error.message }))
    }
    dispatch(inputActions.updateCalculation({ [name]: convertValue }))
  }

  const calculate = (payload) => {
    dispatch(inputActions.updateCalculation({ ...payload }))
  }

  const onChangeHoldownPostBaseFasteners = (e) => {
    const { name, value, selected: isCurrentSelected } = e?.target || {}
    let newValues = [value]

    if (holdownDesign) {
      if (value === HOLDOWN_FASTENERS.ALL) {
        // When checkbox “All” is selected, other checkboxes will be unchecked.
        newValues = [HOLDOWN_FASTENERS.ALL]
      } else {
        newValues = [...holdownFastenersType]

        if (!isCurrentSelected) {
          newValues.push(value)
        } else {
          newValues = newValues.filter((optValue) => optValue !== value)
        }

        if (newValues.includes(HOLDOWN_FASTENERS.ALL)) {
          // When checkboxes other than “All” are selected, “All” will be unchecked.
          newValues = newValues.filter((optValue) => optValue !== HOLDOWN_FASTENERS.ALL)
        } else if (
          newValues.length === holdownFastenersTypeData.length - 1 ||
          newValues.length === 0
        ) {
          // When *all* checkboxes other than “All” are selected, “All” will be checked automatically, and the other checkboxes should be unchecked automatically.
          newValues = [HOLDOWN_FASTENERS.ALL]
        }
      }
    } else if (postBaseDesign) {
      const options = postBaseFastenersTypeOptions[postBaseSubType]
      const ALL = options.find((opt) => opt.text === 'All')?.value
      if (value === ALL) {
        // When checkbox “All” is selected, other checkboxes will be unchecked.
        newValues = [ALL]
      } else {
        newValues = [...postBaseFastenersType]

        if (!isCurrentSelected) {
          newValues.push(value)
        } else {
          newValues = newValues.filter((optValue) => optValue !== value)
        }

        if (newValues.includes(ALL)) {
          // When checkboxes other than “All” are selected, “All” will be unchecked.
          newValues = newValues.filter((optValue) => optValue !== ALL)
        } else if (newValues.length === options.length - 1 || newValues.length === 0) {
          // When *all* checkboxes other than “All” are selected, “All” will be checked automatically, and the other checkboxes should be unchecked automatically.
          newValues = [ALL]
        }
      }
    }

    const payload = { [name]: newValues }
    calculate(payload)
  }

  const onChangeCBMaterialType = (e) => {
    const { name, value } = e.target
    let payload = {}

    if (value === MATERIAL_TYPE.CMU) {
      payload = {
        [name]: value,
        anchorageOption: ANCHORAGE_OPTION.CAST_IN_PLACE,
        geometryType: GEOMETRY_TYPE.STEMWALL,
        geometrySubType: '1',
      }
    } else {
      payload = { [name]: value }
    }

    calculate(payload)
  }

  const onChangeCBGeometryType = (e) => {
    const { name, value } = e.target
    let payload = {}

    if (
      value === GEOMETRY_TYPE.ELEVATED_SLAB ||
      value === GEOMETRY_TYPE.INTERIOR_THICKENED_FOOTING
    ) {
      payload = { [name]: value, geometrySubType: '4' }
    } else if (value === GEOMETRY_TYPE.GARAGE_SLAB || value === GEOMETRY_TYPE.GARAGE_STEMWALL) {
      payload = { [name]: value, geometrySubType: '2' }
    } else {
      payload = { [name]: value, geometrySubType: '1' }
    }

    calculate(payload)
  }

  const onChangeCBFoundationDesign = (name, value) => {
    let payload = {}

    if (postBaseDesign) {
      payload = { ...POST_BASE_DEFAULT_STATE }
    }

    if (!value && !holdownDesign) {
      // do not accept both designs unchecked
      return // skip
    }
    if ((!value && holdownDesign) || (value && !holdownDesign)) {
      payload = { ...payload, [name]: value, holdownSubType: HOLDOWN_SUBTYPE.EMBEDDED_ANCHOR }
    } else {
      payload = { ...payload, [name]: value }
    }

    calculate({ ...payload })
  }

  const onChangeCBHoldownDesign = (name, value) => {
    if (!foundationDesign && !value) return

    let payload = {}

    if (postBaseDesign) {
      payload = {
        ...POST_BASE_DEFAULT_STATE,
        postBaseFastenersType: [POST_BASE_FASTENERS_TYPE.ALL],
      }
    }

    if (!value && !foundationDesign) {
      // skip
    } else if (value && foundationDesign && holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP) {
      // 1B
      payload = {
        ...payload,
        [name]: value,
        holdownFastenersType: [HOLDOWN_FASTENERS.ALL],
        holdownSubType: HOLDOWN_SUBTYPE.EMBEDDED_STRAP,
        geometryType: GEOMETRY_TYPE.STEMWALL,
        geometrySubType: '1',
      }
    } else if (!value && foundationDesign && holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP) {
      // from 1B to 1 C/D
      payload = { ...payload, [name]: value, holdownSubType: HOLDOWN_SUBTYPE.EMBEDDED_ANCHOR }
    } else {
      payload = { ...payload, [name]: value }
    }

    calculate({ ...payload })
  }

  const onChangeCBPostBaseDesign = (postBaseDesign, checked) => {
    // do not accept three designs unchecked
    const allThreeDesignsUnchecked = !checked && !holdownDesign && !foundationDesign
    if (allThreeDesignsUnchecked) return // skip

    const keepValueItems = ['showAllModels', 'searchValue', 'settings']
    const resetState = omit(inputSlice.getInitialState(), keepValueItems)
    const payload = Object.assign({}, resetState, {
      [postBaseDesign]: checked,
      holdownDesign: false,
      lumberSpecies: getLumberSpeciesDefaultValue(country),
      postBaseFastenersType: [POST_BASE_FASTENERS_TYPE.ALL],
      postWidth:
        postWidthData.postbase.lumberSpecies.douglasFirLarchOptions.find((item) => item.default)
          ?.value || POST_WIDTH_POST_BASE_DEFAULT,
      postThickness:
        postThicknessData.postbase.lumberSpecies.douglasFirLarchOptions.find((item) => item.default)
          ?.value || POST_THICKNESS_HOLDOWN_DEFAULT,
    })

    calculate(payload)
  }

  const onChangeCBHoldownSubType = (name, value) => {
    let payload = {}

    if (holdownSubType === value) {
      return
    }

    if (value === HOLDOWN_SUBTYPE.EMBEDDED_STRAP) {
      payload = {
        [name]: value,
        geometryType: GEOMETRY_TYPE.STEMWALL,
        geometrySubType: '1',
        compressiveStrength: COMPRESSIVE_STRENGTH.PSI2500,
        materialType: MATERIAL_TYPE.CONCRETE,
        foundationDesign: true,
      }
    } else {
      payload = { [name]: value }
    }

    calculate(payload)
  }

  const onChangeCBPostBaseSubType = (name, value) => {
    if (postBaseSubType === value) {
      return
    }

    let payload = {}

    const currentPBFastenersTypeOptions = postBaseFastenersTypeOptions[value] ?? []
    const defaultPBFastenersType = [currentPBFastenersTypeOptions[0]?.value]

    const validPBFastenersTypes = postBaseFastenersType?.filter((val) =>
      currentPBFastenersTypeOptions.some((opt) => opt.value === val),
    )

    payload = {
      [name]: value,
      postBaseFastenersType:
        validPBFastenersTypes?.length > 0 ? validPBFastenersTypes : defaultPBFastenersType,
      postBaseLumberSpecies: postBaseLumberSpeciesOptions[country][value].find(
        (item) => item.value === postBaseLumberSpecies,
      )
        ? postBaseLumberSpecies
        : POST_BASE_LUMBER_SPECIES[country].DOUGLAS_FIR_LARCH,
      postBaseMaterialType: postBaseMaterialTypeOptions[value].find(
        (item) => item.value === postBaseMaterialType,
      )
        ? postBaseMaterialType
        : POST_BASE_MATERIAL_TYPE.CONCRETE,
    }
    calculate(payload)
  }

  const onChangeCBAnchorageOption = (e) => {
    const { name, value } = e.target
    let payload = {}

    if (anchorageOption === ANCHORAGE_OPTION.MECHANICAL && value === '1') {
      payload = { [name]: value }
    } else if (anchorageOption === ANCHORAGE_OPTION.ADHESIVE && value === '2') {
      if (geometryType === GEOMETRY_TYPE.STEMWALL && geometrySubType === '2') {
        payload = { [name]: value, geometrySubType: '1' }
      } else {
        payload = { [name]: value }
      }
    } else {
      // handle 2 -> 0, 1 -> 0, 0 -> 1, 0 -> 2
      const geometryTypeList = getGeometryTypeOptions(materialType, value).options
      const findGeometryType = geometryTypeList.find((entry) => entry.value === geometryType)

      if (findGeometryType) {
        if (value === '0') {
          payload = { [name]: value, compressiveStrength: COMPRESSIVE_STRENGTH.PSI2500 }
        } else {
          if (geometryType === GEOMETRY_TYPE.STEMWALL && geometrySubType === '2') {
            // handle 0 -> 2 (Corner Anchor difference)
            payload = { [name]: value, geometrySubType: '1' }
          } else {
            payload = { [name]: value }
          }
        }
      } else {
        // unable to find GeometryType in the new list, set the default geometryType: '2', geometrySubType: '1'
        if (value === '0') {
          payload = {
            [name]: value,
            compressiveStrength: COMPRESSIVE_STRENGTH.PSI2500,
            geometryType: GEOMETRY_TYPE.STEMWALL,
            geometrySubType: '1',
          }
        } else {
          payload = { [name]: value, geometryType: GEOMETRY_TYPE.STEMWALL, geometrySubType: '1' }
        }
      }
    }

    calculate(payload)
  }

  const onChangeCB = (name, value) => {
    const payload = { [name]: value }
    calculate(payload)
  }

  const onChangeDropdown = (e) => {
    const { name, value } = e.target
    const payload = { [name]: value }
    calculate(payload)
  }

  const holdownDesignObj = useLogicButton(
    INPUT_NAME.HOLDOWN_DESIGN,
    holdownDesign,
    onChangeCBHoldownDesign,
  )

  const foundationDesignObj = useLogicButton(
    INPUT_NAME.FOUNDATION_DESIGN,
    foundationDesign,
    onChangeCBFoundationDesign,
  )
  const postBaseDesignObj = useLogicButton(
    INPUT_NAME.POST_BASE_DESIGN,
    postBaseDesign,
    onChangeCBPostBaseDesign,
  )

  // post & holdown
  const holdownSubTypeAnchorObj = useTextButton(
    INPUT_NAME.HOLDOWN_SUBTYPE,
    holdownSubType,
    HOLDOWN_SUBTYPE.EMBEDDED_ANCHOR,
    onChangeCBHoldownSubType,
    isLoading,
  )

  const holdownSubTypeStrapObj = useTextButton(
    INPUT_NAME.HOLDOWN_SUBTYPE,
    holdownSubType,
    HOLDOWN_SUBTYPE.EMBEDDED_STRAP,
    onChangeCBHoldownSubType,
    isLoading,
  )

  // post & post base
  const postBaseSubTypePostInstalledObj = useTextButton(
    INPUT_NAME.POST_BASE_SUBTYPE,
    postBaseSubType,
    POST_BASE_SUBTYPE.POST_INSTALLED,
    onChangeCBPostBaseSubType,
    isLoading,
  )

  const postBaseSubTypeCastInPlaceObj = useTextButton(
    INPUT_NAME.POST_BASE_SUBTYPE,
    postBaseSubType,
    POST_BASE_SUBTYPE.CAST_IN_PLACE,
    onChangeCBPostBaseSubType,
    isLoading,
  )

  // const geometryTypeImageRef = useRef(null)
  const geometryTypeImageSrc = geometryTypeImages(holdownSubType, materialType)[geometryType]

  const onChangeCBCorrosionResistance = (event) => {
    const { name, value, selected } = event?.target || {}
    let newValues = [value]

    if (value === CORROSION_RESISTANCE_VALUE.ALL) {
      // When checkbox “All” is selected, other checkboxes will be unchecked.
      newValues = [CORROSION_RESISTANCE_VALUE.ALL]
    } else {
      newValues = [...corrosionResistance]

      if (!selected) {
        newValues.push(value)
      } else {
        newValues = newValues.filter((optValue) => optValue != value)
      }

      if (newValues.includes(CORROSION_RESISTANCE_VALUE.ALL)) {
        // When checkboxes other than “All” are selected, “All” will be unchecked.
        newValues = newValues.filter((optValue) => optValue != CORROSION_RESISTANCE_VALUE.ALL)
      } else if (newValues.length === 3 || newValues.length === 0) {
        // When *all* checkboxes other than “All” are selected, “All” will be checked automatically, and the other checkboxes should be unchecked automatically.
        newValues = [CORROSION_RESISTANCE_VALUE.ALL]
      }
    }

    onChangeCB(name, newValues)
  }

  const geometryTypeOptions =
    holdownDesign && foundationDesign && holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP
      ? getGeometryTypeOptions(materialType, anchorageOption).optionsB
      : getGeometryTypeOptions(materialType, anchorageOption).options

  const compressiveStrengthOptions =
    holdownDesign && foundationDesign && holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP
      ? compressiveStrengthData.optionsB
      : compressiveStrengthData.options

  const formGroupCorrosionResistance = (
    <FormGroup>
      <FormLabel
        htmlFor={INPUT_NAME.CORROSION_RESISTANCE}
        popperText={helpText.corrosionResistance}
      >
        Corrosion Resistance
      </FormLabel>
      <Dropdown
        id={INPUT_NAME.CORROSION_RESISTANCE}
        name={INPUT_NAME.CORROSION_RESISTANCE}
        value={corrosionResistance}
        options={corrosionResistanceOptions}
        onChange={onChangeCBCorrosionResistance}
        data-testid={INPUT_NAME.CORROSION_RESISTANCE}
        multiple
      />
    </FormGroup>
  )

  const getPostWidthValue = (lumberSpecies) => {
    const postWidthOptions = getPostWidthOptions(holdownDesign, lumberSpecies)

    return postWidthOptions.find((opt) => opt.value === postWidth)
      ? postWidth
      : getDefaultOptValue(postWidthOptions)
  }

  const getPostThicknessValue = (lumberSpecies) => {
    const postThicknessOptions = getPostThicknessOptions(holdownDesign, lumberSpecies)

    return postThicknessOptions.find((opt) => opt.value === postThickness)
      ? postThickness
      : getDefaultOptValue(postThicknessOptions)
  }

  const onChangePostbaseType = (name, value) => {
    if (
      (name === INPUT_NAME.HOLDOWN_TYPE && holdownType === value) ||
      (name === INPUT_NAME.POST_BASE_TYPE && postBaseType === value)
    ) {
      return
    }

    const postBaseLumberSpeciesValue = postBaseLumberSpeciesOptions[country][value].find(
      (opt) => opt.value === postBaseLumberSpecies,
    )
      ? postBaseLumberSpecies
      : getLumberSpeciesDefaultValue(country)

    calculate({
      [name]: value,
      postBaseLumberSpecies: postBaseLumberSpeciesValue,
      postWidth: getPostWidthValue(postBaseLumberSpeciesValue),
      postThickness: getPostThicknessValue(postBaseLumberSpeciesValue),
    })
  }

  const onCountryChange = ({ name, value }) => {
    calculate({
      lumberSpecies: getLumberSpeciesDefaultValue(value),
    })
  }

  const onChangeFoundationType = (e) => {
    const { name, value } = e.target
    const minStemwallThicknessOptions = getMinStemwallThicknessOptions(holdownDesign, value)

    let payload = {
      [name]: value,
      minStemwallThickness: minStemwallThicknessOptions.find(
        (opt) => opt.value === minStemwallThickness,
      )
        ? minStemwallThickness
        : getDefaultOptValue(minStemwallThicknessOptions),
    }

    if (value === FOUNDATION_TYPE.WOOD) {
      // Delete validation for hidden field when change foundation type
      const validationCopy = omit(validation, ['lateralF2', 'factoredDownloadLRFD'])

      payload = {
        ...payload,
        lateralF2: '0',
        factoredDownloadLRFD: '0',
        downloadDuration: DOWNLOAD_DURATION.STANDARD,
        validation: validationCopy,
      }
    }

    calculate(payload)
  }

  const onChangeLumberSpecies = (e) => {
    const { name, value } = e.target
    let payload = { [name]: value }

    if (country === COUNTRY.CANADA) {
      payload = {
        ...payload,
        postWidth: getPostWidthValue(value),
        postThickness: getPostThicknessValue(value),
      }
    }

    calculate(payload)
  }

  return (
    <div className="inputSectionWrapper" {...props}>
      <h2 className="sectionFlag justify-between">
        <span>Input</span>
        <CountrySelection onChange={onCountryChange} />
      </h2>
      <div className="inputSection">
        <div className="container-fluid p-0">
          <Accordion title="Condition">
            <div id="pfdInputCondition">
              <div className="buttonGroup row">
                <div className="col-12 col-sm-4 col-md-4 col-lg-4 col-xl-4 col-xxl-4">
                  <Button
                    variant="subAppSelector"
                    className={concatClasses(
                      'button',
                      '-shadow',
                      postBaseDesignObj.value ? ' -checked' : '',
                    )}
                    {...postBaseDesignObj}
                    data-testid={INPUT_NAME.POST_BASE_DESIGN}
                    disabled={isLoading}
                  >
                    <img
                      className="illustration"
                      src="https://ssttoolbox.widen.net/content/tbw9g7a40s/svg/PFD_PB_PostBase.svg"
                    />
                    <span className="button__text">{POST_BASE_LABELS[country]}</span>
                  </Button>
                </div>
                <div className="col-12 col-sm-4 col-md-4 col-lg-4 col-xl-4 col-xxl-4">
                  <Button
                    variant="subAppSelector"
                    className={concatClasses(
                      'button',
                      '-shadow',
                      holdownDesignObj.value ? ' -checked' : '',
                    )}
                    {...holdownDesignObj}
                    data-testid={INPUT_NAME.HOLDOWN_DESIGN}
                    disabled={isLoading}
                  >
                    <img
                      className="illustration"
                      src="https://embed.widencdn.net/svg/ssttoolbox/cqz6pjkfv5/C_HDB_InstSeq_Ill_Inst_Sitting-on-plate_C0_STEP01.svg"
                    />
                    <span className="button__text">{HOLDOWN_LABELS[country]}</span>
                  </Button>
                </div>
                <div className="col-12 col-sm-4 col-md-4 col-lg-4 col-xl-4 col-xxl-4">
                  <Button
                    variant="subAppSelector"
                    className={concatClasses(
                      'button',
                      '-shadow',
                      foundationDesignObj.value ? ' -checked' : '',
                    )}
                    {...foundationDesignObj}
                    data-testid={INPUT_NAME.FOUNDATION_DESIGN}
                    disabled={country === COUNTRY.CANADA || isLoading}
                  >
                    <img
                      className="illustration"
                      src="https://embed.widencdn.net/svg/ssttoolbox/abe7peeedl/foundation-design-wall.svg"
                      alt="foundation-design-wall"
                    />
                    <span className="button__text">Anchorage</span>
                  </Button>
                </div>
              </div>
            </div>
          </Accordion>
          <Accordion title="Job Settings">
            <div id="pfdInputLoadDemand">
              {country === COUNTRY.USA && (
                <div className="grid grid__auto--200 grid--gap-1-2">
                  <FormGroup>
                    <FormLabel
                      htmlFor={INPUT_NAME.UPLIFT_LOAD_ASD}
                      popperText={helpText.upliftLoadASD}
                    >
                      Uplift Load (160)(ASD)
                    </FormLabel>
                    <FormControl
                      name={INPUT_NAME.UPLIFT_LOAD_ASD}
                      value={formatNumber(upliftLoadASD)}
                      onChange={handleInputChange}
                      error={validation.upliftLoadASD}
                      data-testid={INPUT_NAME.UPLIFT_LOAD_ASD}
                      addonAfter={MEASUREMENT_UNITS.LBS}
                    />
                  </FormGroup>
                  {postBaseDesign &&
                    (postBaseSubType !== POST_BASE_SUBTYPE.POST_INSTALLED ||
                      postBaseMaterialType !== POST_BASE_MATERIAL_TYPE.WOOD) && (
                      <FormGroup>
                        <FormLabel htmlFor={INPUT_NAME.DOWNLOAD_ASD}>Download (100)(ASD)</FormLabel>
                        <FormControl
                          name={INPUT_NAME.DOWNLOAD_ASD}
                          value={formatNumber(downloadASD)}
                          onChange={handleInputChange}
                          error={validation.downloadASD}
                          data-testid={INPUT_NAME.DOWNLOAD_ASD}
                          addonAfter={MEASUREMENT_UNITS.LBS}
                        />
                      </FormGroup>
                    )}
                  {postBaseDesign && (
                    <FormGroup>
                      <FormLabel
                        htmlFor={INPUT_NAME.LATERAL_F1}
                        popperText={helpText.lateralF1}
                        popperImg={
                          HELP_IMG[postBaseSubType].LATERAL_F1 ??
                          HELP_IMG[postBaseSubType][postBaseMaterialType].LATERAL_F1
                        }
                        placement="auto"
                      >
                        F1 (160)(ASD)
                      </FormLabel>
                      <FormControl
                        name={INPUT_NAME.LATERAL_F1}
                        value={formatNumber(lateralF1)}
                        onChange={handleInputChange}
                        error={validation.lateralF1}
                        data-testid={INPUT_NAME.LATERAL_F1}
                        addonAfter={MEASUREMENT_UNITS.LBS}
                      />
                    </FormGroup>
                  )}
                  {postBaseDesign &&
                    (postBaseSubType !== POST_BASE_SUBTYPE.POST_INSTALLED ||
                      postBaseMaterialType !== POST_BASE_MATERIAL_TYPE.WOOD) && (
                      <FormGroup>
                        <FormLabel
                          htmlFor={INPUT_NAME.LATERAL_F2}
                          popperText={helpText.lateralF2}
                          popperImg={
                            HELP_IMG[postBaseSubType].LATERAL_F2 ??
                            HELP_IMG[postBaseSubType][postBaseMaterialType].LATERAL_F2
                          }
                          placement="auto"
                        >
                          F2 (160)(ASD)
                        </FormLabel>
                        <FormControl
                          name={INPUT_NAME.LATERAL_F2}
                          value={formatNumber(lateralF2)}
                          onChange={handleInputChange}
                          error={validation.lateralF2}
                          data-testid={INPUT_NAME.LATERAL_F2}
                          addonAfter={MEASUREMENT_UNITS.LBS}
                        />
                      </FormGroup>
                    )}
                  {foundationDesign && (
                    <FormGroup>
                      <FormLabel htmlFor={INPUT_NAME.LOAD_TYPE} popperText={helpText.loadType}>
                        Load Type
                      </FormLabel>
                      <Dropdown
                        name={INPUT_NAME.LOAD_TYPE}
                        value={loadType}
                        options={loadTypeData}
                        onChange={onChangeDropdown}
                        disabled={isOneDropdownOption(loadTypeData)}
                        data-testid={INPUT_NAME.LOAD_TYPE}
                      />
                    </FormGroup>
                  )}
                  {postBaseDesign && postBaseSubType === POST_BASE_SUBTYPE.CAST_IN_PLACE && (
                    <FormGroup>
                      <FormLabel
                        htmlFor={INPUT_NAME.POST_BASE_LOAD_TYPE}
                        popperText={helpText.loadType}
                      >
                        Load Type
                      </FormLabel>
                      <Dropdown
                        name={INPUT_NAME.POST_BASE_LOAD_TYPE}
                        value={postBaseLoadType}
                        options={postBaseLoadTypeData}
                        onChange={onChangeDropdown}
                        disabled={isOneDropdownOption(postBaseLoadTypeData)}
                        data-testid={INPUT_NAME.POST_BASE_LOAD_TYPE}
                      />
                    </FormGroup>
                  )}
                </div>
              )}
              {country === COUNTRY.CANADA && (
                <div className="grid grid__auto--200 grid--gap-1-2">
                  <FormGroup>
                    <FormLabel
                      htmlFor={INPUT_NAME.UPLIFT_DURATION}
                      popperText={helpText.upliftDuration}
                    >
                      Uplift Duration
                    </FormLabel>
                    <Dropdown
                      name={INPUT_NAME.UPLIFT_DURATION}
                      value={upliftDuration}
                      options={upliftDurationData}
                      onChange={onChangeDropdown}
                      data-testid={INPUT_NAME.UPLIFT_DURATION}
                    />
                  </FormGroup>
                  {holdownDesign && holdownType === HOLDOWN_TYPE.CAST_IN_PLACE && (
                    <FormGroup>
                      <FormLabel
                        htmlFor={INPUT_NAME.LOAD_TYPE}
                        popperText={helpText.loadTypeCanada}
                        placement="auto"
                      >
                        Load Type
                      </FormLabel>
                      <Dropdown
                        name={INPUT_NAME.LOAD_TYPE}
                        value={loadType}
                        options={LOAD_TYPE_CANADA}
                        onChange={onChangeDropdown}
                        disabled={isOneDropdownOption(LOAD_TYPE_CANADA)}
                        data-testid={INPUT_NAME.LOAD_TYPE}
                      />
                    </FormGroup>
                  )}
                  <FormGroup>
                    <FormLabel
                      htmlFor={INPUT_NAME.FACTORED_UPLIFT_LRFD}
                      popperText={helpText.factoredUpliftLRFD}
                    >
                      Factored Uplift ({UNITS.LSD})
                    </FormLabel>
                    <FormControl
                      name={INPUT_NAME.FACTORED_UPLIFT_LRFD}
                      value={formatNumber(factoredUpliftLRFD)}
                      onChange={handleInputChange}
                      error={validation.factoredUpliftLRFD}
                      data-testid={INPUT_NAME.FACTORED_UPLIFT_LRFD}
                      addonAfter={MEASUREMENT_UNITS.LBS}
                    />
                  </FormGroup>
                  {postBaseDesign && (
                    <>
                      {foundationType !== FOUNDATION_TYPE.WOOD && (
                        <>
                          <FormGroup>
                            <FormLabel
                              htmlFor={INPUT_NAME.DOWNLOAD_DURATION}
                              popperText={helpText.downloadDuration}
                            >
                              Download Duration
                            </FormLabel>
                            <Dropdown
                              name={INPUT_NAME.DOWNLOAD_DURATION}
                              value={downloadDuration}
                              options={downloadDurationData}
                              onChange={onChangeDropdown}
                              disabled={isOneDropdownOption(downloadDurationData)}
                              data-testid={INPUT_NAME.DOWNLOAD_DURATION}
                            />
                          </FormGroup>
                          <FormGroup>
                            <FormLabel
                              htmlFor={INPUT_NAME.FACTORED_DOWNLOAD_LRFD}
                              popperText={helpText.factoredDownloadLRFD}
                            >
                              Factored Download ({UNITS.LSD})
                            </FormLabel>
                            <FormControl
                              name={INPUT_NAME.FACTORED_DOWNLOAD_LRFD}
                              value={formatNumber(factoredDownloadLRFD)}
                              onChange={handleInputChange}
                              error={validation.factoredDownloadLRFD}
                              data-testid={INPUT_NAME.FACTORED_DOWNLOAD_LRFD}
                              addonAfter={MEASUREMENT_UNITS.LBS}
                            />
                          </FormGroup>
                        </>
                      )}
                      <FormGroup>
                        <FormLabel
                          htmlFor={INPUT_NAME.LATERAL_F1}
                          popperText={helpText.lateralF1}
                          popperImg={HELP_IMG.CANADA_F1_F2[foundationType]}
                        >
                          F1
                        </FormLabel>
                        <FormControl
                          name={INPUT_NAME.LATERAL_F1}
                          value={formatNumber(lateralF1)}
                          onChange={handleInputChange}
                          error={validation.lateralF1}
                          data-testid={INPUT_NAME.LATERAL_F1}
                          addonAfter={MEASUREMENT_UNITS.LBS}
                        />
                      </FormGroup>
                      {foundationType !== FOUNDATION_TYPE.WOOD && (
                        <FormGroup>
                          <FormLabel
                            htmlFor={INPUT_NAME.LATERAL_F2}
                            popperText={helpText.lateralF2}
                            popperImg={HELP_IMG.CANADA_F1_F2[foundationType]}
                          >
                            F2
                          </FormLabel>
                          <FormControl
                            name={INPUT_NAME.LATERAL_F2}
                            value={formatNumber(lateralF2)}
                            onChange={handleInputChange}
                            error={validation.lateralF2}
                            data-testid={INPUT_NAME.LATERAL_F2}
                            addonAfter={MEASUREMENT_UNITS.LBS}
                          />
                        </FormGroup>
                      )}
                    </>
                  )}
                  {formGroupCorrosionResistance}
                  {holdownDesign && (
                    <FormGroup>
                      <FormLabel
                        htmlFor={INPUT_NAME.MIN_STEMWALL_THICKNESS}
                        popperText={helpText.minStemwallThickness}
                      >
                        Stemwall Thickness
                      </FormLabel>
                      <Dropdown
                        name={INPUT_NAME.MIN_STEMWALL_THICKNESS}
                        value={minStemwallThickness}
                        options={getMinStemwallThicknessOptions(holdownDesign, foundationType)}
                        onChange={onChangeDropdown}
                        data-testid={INPUT_NAME.MIN_STEMWALL_THICKNESS}
                      />
                    </FormGroup>
                  )}
                </div>
              )}
            </div>
          </Accordion>
          {holdownDesignObj.value ? (
            <Accordion title="Post & Holdown">
              <div id="pfdInputPostCriteria">
                {country === COUNTRY.USA && (
                  <>
                    <div className="buttonGroup row">
                      <div className="col">
                        <Button
                          variant="subAppSelector"
                          className={
                            'button -shadow' +
                            (holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_ANCHOR ? ' -checked' : '')
                          }
                          data-testid="holdownSubType.embeddedAnchor"
                          {...holdownSubTypeAnchorObj}
                        >
                          <img
                            className="illustration"
                            src="https://embed.widencdn.net/svg/ssttoolbox/inscbvdyhz/C_HDU5_InstIll_Ill_Instld_CN.svg?u=oviqdm"
                            alt="C_HDU5_InstIll_Ill_Instld_CN"
                          />
                          <span className="button__text">Embedded Anchor</span>
                        </Button>
                      </div>
                      <div className="col">
                        <Popper
                          trigger={['hover', 'hover']}
                          delay={0}
                          overlay={
                            <Tooltip>
                              <p className="m-0">
                                Embedded Strap requires both Design Post & Holdown and Design
                                Anchorage conditions.
                              </p>
                            </Tooltip>
                          }
                        >
                          <Button
                            variant="subAppSelector"
                            className={
                              'button -shadow' +
                              (holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP ? ' -checked' : '')
                            }
                            data-testid="holdownSubType.embeddedStrap"
                            {...holdownSubTypeStrapObj}
                          >
                            <img
                              className="illustration"
                              src="https://embed.widencdn.net/svg/ssttoolbox/kx7kwhupce/STHD_midwall_in.svg"
                              alt="STHD_midwall_in"
                            />
                            <span className="button__text">Embedded Strap</span>
                          </Button>
                        </Popper>
                      </div>
                    </div>
                    <div className="grid grid__auto--200 grid--gap-1-2">
                      <FormGroup>
                        <FormLabel
                          htmlFor={INPUT_NAME.HOLDOWN_FASTENERS_TYPE}
                          popperText={helpText.holdownFastenersType}
                        >
                          Post Fasteners
                        </FormLabel>
                        <Dropdown
                          name={INPUT_NAME.HOLDOWN_FASTENERS_TYPE}
                          value={holdownFastenersType}
                          options={holdownFastenersTypeData}
                          onChange={onChangeHoldownPostBaseFasteners}
                          disabled={isOneDropdownOption(holdownFastenersTypeData)}
                          data-testid={INPUT_NAME.HOLDOWN_FASTENERS_TYPE}
                          multiple
                        />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel htmlFor={INPUT_NAME.LUMBER_SPECIES}>Lumber Species</FormLabel>
                        <Dropdown
                          name={INPUT_NAME.LUMBER_SPECIES}
                          value={lumberSpecies}
                          options={holdownLumberSpeciesOptions[country]}
                          disabled={isOneDropdownOption(holdownLumberSpeciesOptions[country])}
                          onChange={onChangeLumberSpecies}
                          data-testid={INPUT_NAME.LUMBER_SPECIES}
                        />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel htmlFor={INPUT_NAME.WALL_THICKNESS}>
                          Stemwall Thickness
                        </FormLabel>
                        <Dropdown
                          name={INPUT_NAME.WALL_THICKNESS}
                          value={wallThickness}
                          options={wallThicknessData}
                          onChange={onChangeDropdown}
                          disabled={isOneDropdownOption(wallThicknessData)}
                          data-testid={INPUT_NAME.WALL_THICKNESS}
                        />
                      </FormGroup>
                      {holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP ? (
                        <FormGroup>
                          <FormLabel
                            htmlFor={INPUT_NAME.INSTALL_OVER_SHEATHING}
                            popperText={helpText.installOverSheathing}
                          >
                            Install Over 1/2&quot; Sheathing
                          </FormLabel>
                          <Dropdown
                            name={INPUT_NAME.INSTALL_OVER_SHEATHING}
                            value={installOverSheathing}
                            options={installOverSheathingData}
                            onChange={onChangeDropdown}
                            disabled={isOneDropdownOption(installOverSheathingData)}
                            data-testid={INPUT_NAME.INSTALL_OVER_SHEATHING}
                          />
                        </FormGroup>
                      ) : null}
                    </div>
                  </>
                )}
                {country === COUNTRY.CANADA && (
                  <>
                    <div className="buttonGroup row">
                      {postTypeData.map((postType) => (
                        <div key={postType.value} className="col-12 col-sm-6 col-md-6 col-lg-6">
                          <Button
                            variant="subAppSelector"
                            className={concatClasses(
                              'button',
                              '-shadow',
                              holdownType === postType.value ? ' -checked' : '',
                            )}
                            onClick={() =>
                              onChangePostbaseType(INPUT_NAME.HOLDOWN_TYPE, postType.value)
                            }
                            disabled={isLoading}
                          >
                            <img
                              className="illustration"
                              src={HOLD_DOWN_TYPE_IMG_SRC[postType.value]}
                            />
                            <span className="button__text">{postType.text}</span>
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid__auto--200 grid--gap-1-2">
                      <div className="grid grid__auto--200 grid--gap-2 grid--full-width">
                        <div className="grid grid--cols-subgrid grid--full-width">
                          <FormGroup>
                            <FormLabel htmlFor={INPUT_NAME.LUMBER_SPECIES}>
                              Lumber Species
                            </FormLabel>
                            <Dropdown
                              id={INPUT_NAME.LUMBER_SPECIES}
                              name={INPUT_NAME.LUMBER_SPECIES}
                              value={lumberSpecies}
                              options={holdownLumberSpeciesOptions[country][holdownType]}
                              onChange={onChangeLumberSpecies}
                              data-testid={INPUT_NAME.LUMBER_SPECIES}
                            />
                          </FormGroup>
                          <FormGroup>
                            <FormLabel
                              htmlFor={INPUT_NAME.HOLDOWN_FASTENERS_TYPE}
                              popperText={helpText.holdownFastenersType}
                            >
                              Post Fasteners
                            </FormLabel>
                            <Dropdown
                              name={INPUT_NAME.HOLDOWN_FASTENERS_TYPE}
                              value={holdownFastenersType}
                              options={holdownFastenersTypeData}
                              onChange={onChangeHoldownPostBaseFasteners}
                              disabled={isOneDropdownOption(holdownFastenersTypeData)}
                              data-testid={INPUT_NAME.HOLDOWN_FASTENERS_TYPE}
                              multiple
                            />
                          </FormGroup>
                        </div>
                      </div>
                      <FormGroup>
                        <FormLabel
                          htmlFor={INPUT_NAME.POST_THICKNESS}
                          popperText={helpText.postThickness}
                          popperImg={HELP_IMG.POST_WIDTH_POST_THICKNESS}
                        >
                          Post Thickness
                        </FormLabel>
                        <Dropdown
                          name={INPUT_NAME.POST_THICKNESS}
                          value={postThickness}
                          options={getPostThicknessOptions(holdownDesign, lumberSpecies)}
                          onChange={onChangeDropdown}
                          disabled={isOneDropdownOption(
                            getPostThicknessOptions(holdownDesign, lumberSpecies),
                          )}
                          data-testid={INPUT_NAME.POST_THICKNESS}
                        />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel
                          htmlFor={INPUT_NAME.POST_WIDTH}
                          popperText={helpText.postWidth}
                          popperImg={HELP_IMG.POST_WIDTH_POST_THICKNESS}
                        >
                          Post Width
                        </FormLabel>
                        <Dropdown
                          name={INPUT_NAME.POST_WIDTH}
                          value={postWidth}
                          options={getPostWidthOptions(holdownDesign, lumberSpecies)}
                          onChange={onChangeDropdown}
                          disabled={isOneDropdownOption(
                            getPostWidthOptions(holdownDesign, lumberSpecies),
                          )}
                          data-testid={INPUT_NAME.POST_WIDTH}
                        />
                      </FormGroup>
                      {holdownType === HOLDOWN_TYPE.CAST_IN_PLACE && (
                        <FormGroup>
                          <FormLabel htmlFor={INPUT_NAME.INSTALLATION_TYPE}>
                            Installation Location
                          </FormLabel>
                          <Dropdown
                            name={INPUT_NAME.INSTALLATION_TYPE}
                            value={installationType}
                            options={installationTypeData}
                            onChange={onChangeDropdown}
                            disabled={isOneDropdownOption(installationTypeData)}
                            data-testid={INPUT_NAME.INSTALLATION_TYPE}
                          />
                        </FormGroup>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Accordion>
          ) : null}
          {foundationDesignObj.value ? (
            <Accordion title="Anchorage">
              <div id="pfdInputAnchorageCriteria">
                <div className="grid grid__auto--200 grid--gap-1-2">
                  <FormGroup>
                    <FormLabel htmlFor={INPUT_NAME.MATERIAL_TYPE}>Base Material</FormLabel>
                    <Dropdown
                      name={INPUT_NAME.MATERIAL_TYPE}
                      value={materialType}
                      options={materialTypeOptions[holdownSubType]}
                      onChange={onChangeCBMaterialType}
                      data-testid={INPUT_NAME.MATERIAL_TYPE}
                      disabled={isOneDropdownOption(materialTypeOptions[holdownSubType])}
                    />
                  </FormGroup>
                  {materialType === MATERIAL_TYPE.CONCRETE ? (
                    <FormGroup>
                      <FormLabel
                        htmlFor={INPUT_NAME.COMPRESSIVE_STRENGTH}
                        popperText={helpText.compressiveStrength}
                      >
                        Compressive Strength
                      </FormLabel>
                      <Dropdown
                        name={INPUT_NAME.COMPRESSIVE_STRENGTH}
                        value={compressiveStrength}
                        options={compressiveStrengthOptions}
                        disabled={isOneDropdownOption(compressiveStrengthOptions)}
                        onChange={onChangeDropdown}
                        data-testid={INPUT_NAME.COMPRESSIVE_STRENGTH}
                      />
                    </FormGroup>
                  ) : null}
                  {holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_ANCHOR ? (
                    <FormGroup>
                      <FormLabel htmlFor={INPUT_NAME.ANCHORAGE_OPTION}>Anchor Type</FormLabel>
                      <Dropdown
                        name={INPUT_NAME.ANCHORAGE_OPTION}
                        value={anchorageOption}
                        options={anchorageOptions[materialType]}
                        disabled={isOneDropdownOption(anchorageOptions[materialType])}
                        onChange={onChangeCBAnchorageOption}
                        data-testid={INPUT_NAME.ANCHORAGE_OPTION}
                      />
                    </FormGroup>
                  ) : null}
                  {holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP ? (
                    <FormGroup>
                      <FormLabel
                        htmlFor={INPUT_NAME.RAISED_FLOOR}
                        popperText={helpText.raisedFloor}
                      >
                        Raised Floor
                      </FormLabel>
                      <Dropdown
                        name={INPUT_NAME.RAISED_FLOOR}
                        value={raisedFloor}
                        options={raisedFloorData}
                        onChange={onChangeDropdown}
                        data-testid={INPUT_NAME.RAISED_FLOOR}
                      />
                    </FormGroup>
                  ) : null}
                  <FormGroup>
                    <FormLabel
                      htmlFor={INPUT_NAME.GEOMETRY_TYPE}
                      popperText={helpText.geometryType}
                    >
                      Foundation Type
                    </FormLabel>
                    <Dropdown
                      name={INPUT_NAME.GEOMETRY_TYPE}
                      value={geometryType}
                      options={geometryTypeOptions}
                      onChange={onChangeCBGeometryType}
                      disabled={isOneDropdownOption(geometryTypeOptions)}
                      data-testid={INPUT_NAME.GEOMETRY_TYPE}
                    />
                  </FormGroup>
                  {geometryType != GEOMETRY_TYPE.ELEVATED_SLAB &&
                  geometryType != GEOMETRY_TYPE.INTERIOR_THICKENED_FOOTING ? (
                    <FormGroup>
                      <FormLabel htmlFor={INPUT_NAME.GEOMETRY_SUBTYPE}>
                        Installation Location
                      </FormLabel>
                      <Dropdown
                        name={INPUT_NAME.GEOMETRY_SUBTYPE}
                        value={geometrySubType}
                        options={
                          getGeometrySubTypeOptions(geometryType, materialType, anchorageOption)
                            .options
                        }
                        onChange={onChangeDropdown}
                        disabled={isOneDropdownOption(
                          getGeometrySubTypeOptions(geometryType, materialType, anchorageOption)
                            .options,
                        )}
                        data-testid={INPUT_NAME.GEOMETRY_SUBTYPE}
                      />
                    </FormGroup>
                  ) : null}
                </div>
                {geometryTypeImageSrc && geometryTypeImageSrc.length > 0 ? (
                  <>
                    <h4>Foundation Type Details</h4>

                    <div className="row">
                      {geometryTypeImageSrc.map((item, index) => (
                        <div className="col-6" label="Foundation Type Details" key={'key' + index}>
                          <figure className="figure">
                            <img
                              className="illustration"
                              src={item.url}
                              key={'key' + index}
                              alt=""
                            />
                            <figcaption className="figcaption">{item.label}</figcaption>
                          </figure>
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            </Accordion>
          ) : null}
          {postBaseDesignObj.value ? (
            <>
              <Accordion title="Post & Post Base">
                <div id="pfdInputPostBaseCriteria">
                  {country === COUNTRY.USA && (
                    <>
                      <div className="buttonGroup row">
                        <div className="col">
                          <Button
                            variant="subAppSelector"
                            className={concatClasses(
                              'button',
                              '-shadow',
                              postBaseSubType === POST_BASE_SUBTYPE.POST_INSTALLED
                                ? ' -checked'
                                : '',
                            )}
                            data-testid="postBaseSubType.postInstalled"
                            {...postBaseSubTypePostInstalledObj}
                          >
                            <img
                              className="illustration"
                              src="https://ssttoolbox.widen.net/content/rqvityn9vq/svg/PFD_PB_PostInstalled.svg"
                              alt={POST_BASE_SUBTYPE_LABEL[POST_BASE_SUBTYPE.POST_INSTALLED]}
                            />
                            <span className="button__text">
                              {POST_BASE_SUBTYPE_LABEL[POST_BASE_SUBTYPE.POST_INSTALLED]}
                            </span>
                          </Button>
                        </div>
                        <div className="col">
                          <Button
                            variant="subAppSelector"
                            className={concatClasses(
                              'button',
                              '-shadow',
                              postBaseSubType === POST_BASE_SUBTYPE.CAST_IN_PLACE
                                ? ' -checked'
                                : '',
                            )}
                            data-testid="postBaseSubType.castInPlace"
                            {...postBaseSubTypeCastInPlaceObj}
                          >
                            <img
                              className="illustration"
                              src="https://ssttoolbox.widen.net/content/2synutkutr/svg/PFD_PB_CastInPlace_CBS.svg"
                              alt={POST_BASE_SUBTYPE_LABEL[POST_BASE_SUBTYPE.CAST_IN_PLACE]}
                            />
                            <span className="button__text">
                              {POST_BASE_SUBTYPE_LABEL[POST_BASE_SUBTYPE.CAST_IN_PLACE]}
                            </span>
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid__auto--200 grid--gap-1-2">
                        <FormGroup>
                          <FormLabel
                            htmlFor={INPUT_NAME.POST_BASE_FASTENERS_TYPE}
                            popperText={helpText.postBaseFasteners}
                          >
                            Post Fasteners
                          </FormLabel>
                          <Dropdown
                            name={INPUT_NAME.POST_BASE_FASTENERS_TYPE}
                            value={postBaseFastenersType}
                            options={postBaseFastenersTypeOptions[postBaseSubType]}
                            onChange={onChangeHoldownPostBaseFasteners}
                            data-testid={INPUT_NAME.POST_BASE_FASTENERS_TYPE}
                            multiple
                          />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel htmlFor={INPUT_NAME.POST_BASE_LUMBER_SPECIES}>
                            Lumber Species
                          </FormLabel>
                          <Dropdown
                            name={INPUT_NAME.POST_BASE_LUMBER_SPECIES}
                            value={postBaseLumberSpecies}
                            options={postBaseLumberSpeciesOptions[country][postBaseSubType]}
                            onChange={onChangeLumberSpecies}
                            data-testid={INPUT_NAME.POST_BASE_LUMBER_SPECIES}
                          />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel htmlFor={INPUT_NAME.POST_BASE_MATERIAL_TYPE}>
                            Base Material
                          </FormLabel>
                          <Dropdown
                            name={INPUT_NAME.POST_BASE_MATERIAL_TYPE}
                            value={postBaseMaterialType}
                            options={postBaseMaterialTypeOptions[postBaseSubType]}
                            onChange={onChangeDropdown}
                            data-testid={INPUT_NAME.POST_BASE_MATERIAL_TYPE}
                            disabled={isOneDropdownOption(
                              postBaseMaterialTypeOptions[postBaseSubType],
                            )}
                          />
                        </FormGroup>
                        {postBaseSubType === POST_BASE_SUBTYPE.CAST_IN_PLACE && (
                          <FormGroup>
                            <FormLabel htmlFor={INPUT_NAME.POST_BASE_GEOMETRY_SUBTYPE}>
                              Installation Location
                            </FormLabel>
                            <Dropdown
                              name={INPUT_NAME.POST_BASE_GEOMETRY_SUBTYPE}
                              value={postBaseGeometrySubType}
                              options={postBaseGeometrySubTypeData}
                              onChange={onChangeDropdown}
                              data-testid={INPUT_NAME.POST_BASE_GEOMETRY_SUBTYPE}
                            />
                          </FormGroup>
                        )}
                        {formGroupCorrosionResistance}
                      </div>
                    </>
                  )}
                  {country === COUNTRY.CANADA && (
                    <>
                      <div className="buttonGroup row">
                        {postTypeData.map((postType) => (
                          <div key={postType.value} className="col-12 col-sm-6 col-md-6 col-lg-6">
                            <Button
                              variant="subAppSelector"
                              className={concatClasses(
                                'button',
                                '-shadow',
                                postBaseType === postType.value ? ' -checked' : '',
                              )}
                              onClick={() =>
                                onChangePostbaseType(INPUT_NAME.POST_BASE_TYPE, postType.value)
                              }
                              disabled={isLoading}
                            >
                              <img
                                className="illustration"
                                src={POST_BASE_TYPE_IMG_SRC[postType.value]}
                              />
                              <span className="button__text">{postType.text}</span>
                            </Button>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid__auto--200 grid--gap-1-2">
                        <div className="grid grid__auto--200 grid--gap-2 grid--full-width">
                          <div className="grid grid--cols-subgrid grid--full-width">
                            <FormGroup>
                              <FormLabel htmlFor={INPUT_NAME.POST_BASE_LUMBER_SPECIES}>
                                Lumber Species
                              </FormLabel>
                              <Dropdown
                                id={INPUT_NAME.POST_BASE_LUMBER_SPECIES}
                                name={INPUT_NAME.POST_BASE_LUMBER_SPECIES}
                                value={postBaseLumberSpecies}
                                options={postBaseLumberSpeciesOptions[country][postBaseType]}
                                onChange={onChangeLumberSpecies}
                                data-testid={INPUT_NAME.POST_BASE_LUMBER_SPECIES}
                              />
                            </FormGroup>
                            <FormGroup>
                              <FormLabel
                                htmlFor={INPUT_NAME.POST_BASE_FASTENERS_TYPE}
                                popperText={helpText.postBaseFastenersType}
                              >
                                Post Fasteners
                              </FormLabel>
                              <Dropdown
                                name={INPUT_NAME.POST_BASE_FASTENERS_TYPE}
                                value={postBaseFastenersType}
                                options={
                                  postBaseFastenersTypeOptions[POST_BASE_SUBTYPE.POST_INSTALLED]
                                }
                                onChange={onChangeHoldownPostBaseFasteners}
                                disabled={isOneDropdownOption(
                                  postBaseFastenersTypeOptions[POST_BASE_SUBTYPE.POST_INSTALLED],
                                )}
                                data-testid={INPUT_NAME.POST_BASE_FASTENERS_TYPE}
                                multiple
                              />
                            </FormGroup>
                          </div>
                        </div>
                        <FormGroup>
                          <FormLabel
                            htmlFor={INPUT_NAME.POST_THICKNESS}
                            popperText={helpText.postBasePostThicknessPostWidth}
                          >
                            Post Thickness
                          </FormLabel>
                          <Dropdown
                            name={INPUT_NAME.POST_THICKNESS}
                            value={postThickness}
                            options={getPostThicknessOptions(holdownDesign, postBaseLumberSpecies)}
                            onChange={onChangeDropdown}
                            disabled={isOneDropdownOption(
                              getPostThicknessOptions(holdownDesign, postBaseLumberSpecies),
                            )}
                            data-testid={INPUT_NAME.POST_THICKNESS}
                          />
                        </FormGroup>
                        <FormGroup>
                          <FormLabel
                            htmlFor={INPUT_NAME.POST_WIDTH}
                            popperText={helpText.postBasePostThicknessPostWidth}
                          >
                            Post Width
                          </FormLabel>
                          <Dropdown
                            name={INPUT_NAME.POST_WIDTH}
                            value={postWidth}
                            options={getPostWidthOptions(holdownDesign, postBaseLumberSpecies)}
                            onChange={onChangeDropdown}
                            disabled={isOneDropdownOption(
                              getPostWidthOptions(holdownDesign, postBaseLumberSpecies),
                            )}
                            data-testid={INPUT_NAME.POST_WIDTH}
                          />
                        </FormGroup>
                      </div>
                    </>
                  )}
                </div>
              </Accordion>
              {country === COUNTRY.CANADA && (
                <Accordion title="Foundation">
                  <div>
                    <div className="grid grid__auto--200 grid--gap-1-2">
                      <FormGroup>
                        <FormLabel htmlFor={INPUT_NAME.FOUNDATION_TYPE}>Foundation Type</FormLabel>
                        <Dropdown
                          name={INPUT_NAME.FOUNDATION_TYPE}
                          value={foundationType}
                          options={foundationTypeData}
                          onChange={onChangeFoundationType}
                          disabled={isOneDropdownOption(foundationTypeData)}
                          data-testid={INPUT_NAME.FOUNDATION_TYPE}
                        />
                      </FormGroup>
                      <FormGroup>
                        <FormLabel
                          htmlFor={INPUT_NAME.MIN_STEMWALL_THICKNESS}
                          popperText={helpText.minStemwallThickness}
                        >
                          Stemwall Thickness
                        </FormLabel>
                        <Dropdown
                          name={INPUT_NAME.MIN_STEMWALL_THICKNESS}
                          value={minStemwallThickness}
                          options={getMinStemwallThicknessOptions(holdownDesign, foundationType)}
                          onChange={onChangeDropdown}
                          data-testid={INPUT_NAME.MIN_STEMWALL_THICKNESS}
                        />
                      </FormGroup>
                    </div>
                  </div>
                </Accordion>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default Input
